import { useMemo } from "react"
import { useSubscription } from "react-relay"
import { ConnectionHandler, graphql, GraphQLSubscriptionConfig, ROOT_ID } from "relay-runtime"
import {
  useOnChatChangedSubscription as useOnChatChangedSubscriptionType,
  useOnChatChangedSubscription$data,
} from "@/modules/chats/services/__generated__/useOnChatChangedSubscription.graphql"
import { ChatMessagesConnectionKey } from "@/modules/chats/services/useChatMessagesQuery"

const subscription = graphql`
  subscription useOnChatChangedSubscription {
    onChatChanged {
      lastMessage {
        ...ChatLastMessageFragment @relay(mask: false)
      }
      readState {
        ...ChatReadStateFragment @relay(mask: false)
      }
      receivedMessage {
        threadId
        senderKind
        message {
          ...ChatMessageFragment @relay(mask: false)
        }
      }
      greeting {
        ...ChatGreetingFragment @relay(mask: false)
      }
    }
  }
`

export const useOnChatChangedSubscription = (
  onNext: (data: useOnChatChangedSubscription$data | null | undefined) => void,
) => {
  const config: GraphQLSubscriptionConfig<useOnChatChangedSubscriptionType> = useMemo(
    () => ({
      subscription,
      variables: {},
      updater: (store) => {
        const event = store.getRootField("onChatChanged")
        const receivedMessage = event?.getLinkedRecord("receivedMessage")
        const threadId = receivedMessage?.getValue("threadId")?.toString()
        const messageRecord = receivedMessage?.getLinkedRecord("message")
        if (!threadId || !messageRecord) return

        const connectionId = ConnectionHandler.getConnectionID(ROOT_ID, ChatMessagesConnectionKey, { threadId })
        const connection = store.get(connectionId)
        if (!connection) return

        const messageId = messageRecord.getDataID()
        const edges = connection.getLinkedRecords("edges") ?? []
        const alreadyPresent = edges.some((edge) => edge.getLinkedRecord("node")?.getDataID() === messageId)
        if (alreadyPresent) return

        const newEdge = ConnectionHandler.createEdge(store, connection, messageRecord, "ChatMessageEdge")
        ConnectionHandler.insertEdgeBefore(connection, newEdge)
      },
      onNext,
      onError: console.log,
      cacheConfig: { metadata: { sensitive: true } },
    }),
    [onNext],
  )

  return useSubscription<useOnChatChangedSubscriptionType>(config)
}
